<script setup>
import Card from '@/Components/Card/Card.vue';
import { inject } from 'vue';
import ExplainScorePart from '@/Pages/DomainScore/Partials/DetailedReport/ExplainScorePart.vue';
import ActionType from '@/Common/Enum/ActionType.js';
import { API_STATUS } from '@/Common/Enum/ApiStatus.js';
import LinkDomainAnalysis from '@/Pages/DomainScore/Partials/DetailedReport/LinkDomainAnalysis.vue';
import DetailedReportHeader from '@/Pages/DomainScore/Partials/DetailedReport/DetailedReportHeader.vue';
import { getSpfDnsRecordStatus } from '@/Common/DnsRecordMessaging.js';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const result = inject('score');

const tags =
  result.records.spf?.tags?.reduce((acc, tag) => {
    acc[tag.msg] = tag;
    return acc;
  }, {}) ?? {};

const lookupMessage = (() => {
  const tagMessages = {
    recursive: {
      message: t(
        'domain_score.partials.detailed_report.impersonation.spf_card.recursive_lookups_found'
      ),
      actionType: ActionType.ACTION_REQUIRED
    },
    'dns>10': {
      message: t(
        'domain_score.partials.detailed_report.impersonation.spf_card.too_many_dns_lookups'
      ),
      actionType: ActionType.ACTION_REQUIRED
    },
    'mxa>10': {
      message: t(
        'domain_score.partials.detailed_report.impersonation.spf_card.too_many_mx_a_records'
      ),
      actionType: ActionType.ACTION_REQUIRED
    },
    'void>2': {
      message: t(
        'domain_score.partials.detailed_report.impersonation.spf_card.too_many_void_lookups'
      ),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'dns>6': {
      message: t(
        'domain_score.partials.detailed_report.impersonation.spf_card.high_dns_lookups'
      ),
      actionType: ActionType.ACTION_RECOMMENDED
    },
    'void>0': {
      message: t(
        'domain_score.partials.detailed_report.impersonation.spf_card.high_void_lookups'
      ),
      actionType: ActionType.ACTION_RECOMMENDED
    }
  };

  let actionType = null;
  let messages = [];

  Object.keys(tags).forEach((tag) => {
    if (tagMessages.hasOwnProperty(tag)) {
      if (actionType === null) {
        actionType = tagMessages[tag].actionType;
      }
      messages.push(tagMessages[tag].message);
    }
  });

  if (!actionType) {
    return {
      actionType: ActionType.NO_ACTION,
      message: t(
        'domain_score.partials.detailed_report.impersonation.spf_card.num_lookups_found',
        { num: result.records.spf.num_lookup }
      )
    };
  }

  return {
    actionType,
    message: t(
      'domain_score.partials.detailed_report.impersonation.spf_card.num_lookups_found_with_messages',
      {
        num: result.records.spf.num_lookup,
        messages: messages.join('. ')
      }
    )
  };
})();

const allMessage = (() => {
  const tagMessages = {
    '~': {
      message: t(
        'domain_score.partials.detailed_report.impersonation.spf_card.all_soft_fail'
      ),
      actionType: ActionType.NO_ACTION
    },
    '-': {
      message: t(
        'domain_score.partials.detailed_report.impersonation.spf_card.all_fail'
      ),
      actionType: ActionType.NO_ACTION
    },
    '?': {
      message: t(
        'domain_score.partials.detailed_report.impersonation.spf_card.all_neutral'
      ),
      actionType: ActionType.ACTION_REQUIRED
    },
    '+': {
      message: t(
        'domain_score.partials.detailed_report.impersonation.spf_card.all_pass'
      ),
      actionType: ActionType.ACTION_REQUIRED
    }
  };

  if (tagMessages.hasOwnProperty(result.records.spf.all_qualifier)) {
    return tagMessages[result.records.spf.all_qualifier];
  }

  return {
    message: t(
      'domain_score.partials.detailed_report.impersonation.spf_card.all_not_specified'
    ),
    actionType: ActionType.ACTION_REQUIRED
  };
})();

const spfRecordStatus = getSpfDnsRecordStatus(result);
</script>

<template>
  <Card class="bg-white-opacity">
    <DetailedReportHeader
      :title="
        $t('domain_score.partials.detailed_report.impersonation.spf_card.title')
      "
      :description="
        $t(
          'domain_score.partials.detailed_report.impersonation.spf_card.description'
        )
      "
      :tooltip="
        $t(
          'domain_score.partials.detailed_report.impersonation.spf_card.tooltip'
        )
      "
    />
    <div>
      <div class="mt-5 flex flex-col">
        <ExplainScorePart
          :title="
            $t(
              'domain_score.partials.detailed_report.impersonation.spf_card.dns_record'
            )
          "
          :action-type="spfRecordStatus.actionType"
        >
          {{ spfRecordStatus.message }}
          <div v-if="spfRecordStatus.canAnalyse">
            &nbsp;-
            <LinkDomainAnalysis :domain="result.domain" fragment="spf" />
          </div>
        </ExplainScorePart>
        <div
          v-if="result.records?.spf?.status !== API_STATUS.FATAL"
          class="flex flex-col"
        >
          <ExplainScorePart
            :title="
              $t(
                'domain_score.partials.detailed_report.impersonation.spf_card.lookups'
              )
            "
            :action-type="lookupMessage.actionType"
          >
            {{ lookupMessage.message }}
          </ExplainScorePart>

          <ExplainScorePart
            :title="
              $t(
                'domain_score.partials.detailed_report.impersonation.spf_card.all'
              )
            "
            :action-type="allMessage.actionType"
          >
            {{ allMessage.message }}
          </ExplainScorePart>
        </div>
      </div>
    </div>
  </Card>
</template>
